import { useState } from 'react';
import { PackageIcon, WarningIcon } from '@phosphor-icons/react';
import { Link } from 'react-router';
import { Tooltip, cn } from '@skupreme/shared/ui';
import { useOrdersV2 } from '../../hooks/orders-v2';
import { StopRowClick } from '../common/StopRowClick';
import {
  getFulfillmentSummary,
  getProviderLogo,
  type FulfillmentOrderItem,
  type FulfillmentProvider,
  type FulfillmentSummary,
} from '../../lib/fulfillment';

export type FulfillmentCellProps = {
  items: FulfillmentOrderItem[] | undefined;
  providers: FulfillmentProvider[] | undefined;
  selectedFP?: FulfillmentProvider;
  /* Marks lines the provider cannot cover today. */
  highlightShortages?: boolean;
  /* Used when the marketplace fulfils the order itself and sends no provider. */
  fallbackLabel?: string;
};

const SECTION_LABEL = 'sk:text-[13px] sk:font-semibold sk:text-fg-tertiary';

function initial(name: string): string {
  return name.trim() ? name.trim()[0].toUpperCase() : '?';
}

function ProviderMark({
  provider,
  name,
}: {
  provider?: FulfillmentProvider;
  name: string;
}) {
  const [failedSources, setFailedSources] = useState<string[]>([]);
  const customLogo = provider?.wmsOrganizationLogo?.url;
  const integrationLogo = provider
    ? getProviderLogo(provider.integration_type)
    : undefined;
  const src = [customLogo, integrationLogo].find(
    candidate => candidate && !failedSources.includes(candidate),
  );

  if (!src) {
    return (
      <span
        className={cn(
          'sk:flex sk:size-4.5 sk:shrink-0 sk:items-center sk:justify-center sk:rounded-full',
          'sk:bg-fill-secondary sk:text-[9px] sk:font-bold sk:text-fg-secondary',
        )}
      >
        {initial(name)}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt=""
      className="sk:size-4.5 sk:shrink-0 sk:rounded-full sk:object-contain"
      onError={() =>
        setFailedSources(current =>
          current.includes(src) ? current : [...current, src],
        )
      }
    />
  );
}

function ProviderHeading({ provider }: { provider: FulfillmentProvider }) {
  const name = provider.name || 'Unnamed provider';
  const city = [provider.address?.city, provider.address?.state]
    .filter(Boolean)
    .join(', ');
  const providerLink = provider._id
    ? `/app/scm/storage-provider/${encodeURIComponent(provider._id)}`
    : undefined;

  return (
    <span className="sk:flex sk:items-center sk:gap-2">
      <ProviderMark provider={provider} name={name} />
      {providerLink ? (
        <Link
          to={providerLink}
          className="sk:min-w-0 sk:truncate sk:font-medium sk:text-link sk:hover:text-link sk:hover:underline"
        >
          {name}
        </Link>
      ) : (
        <span className="sk:min-w-0 sk:truncate sk:font-medium">{name}</span>
      )}
      {city ? (
        <span className="sk:shrink-0 sk:text-fg-tertiary">{city}</span>
      ) : null}
    </span>
  );
}

/* EN-4544: often no warehouse has the stock, which the list does not know; the order page says why. */
const V2_UNASSIGNED = 'No provider assigned yet. Open the order to see why.';

function Breakdown({
  summary,
  fallbackLabel,
}: {
  summary: FulfillmentSummary;
  fallbackLabel?: string;
}) {
  /* EN-4544: on V2 the order page says why nothing is assigned (no stock, or a choice still to make). */
  const { isV2 } = useOrdersV2();
  const unassignedText = isV2 ? V2_UNASSIGNED : 'No provider assigned';
  return (
    <span className="sk:flex sk:w-80 sk:flex-col sk:gap-3">
      <span className="sk:flex sk:flex-col sk:gap-1.5">
        <span className={SECTION_LABEL}>
          {summary.isSplit
            ? `Split across ${summary.providers.length}`
            : 'Ships from'}
        </span>
        {summary.providers.length === 0 ? (
          <span className="sk:text-fg-secondary">
            {fallbackLabel || unassignedText}
          </span>
        ) : (
          summary.providers.map(provider => (
            <ProviderHeading key={provider._id} provider={provider} />
          ))
        )}
      </span>

      <span className="sk:flex sk:flex-col sk:gap-1.5 sk:border-t sk:border-border-subtle sk:pt-3">
        <span className={SECTION_LABEL}>
          {summary.lineCount} products · {summary.unitCount} units
        </span>
        <span className="sk:flex sk:flex-col sk:gap-3">
          {summary.lines.map(line => (
            <span key={line.key} className="sk:flex sk:items-baseline sk:gap-2">
              <span className="sk:flex sk:min-w-0 sk:flex-1 sk:flex-col">
                {line.itemId ? (
                  <Link
                    to={`/app/catalogs/catalog/${encodeURIComponent(line.itemId)}`}
                    className="sk:min-w-0 sk:truncate sk:text-sm sk:text-link sk:hover:text-link sk:hover:underline"
                  >
                    {line.title}
                  </Link>
                ) : (
                  <span className="sk:min-w-0 sk:truncate sk:text-sm">
                    {line.title}
                  </span>
                )}
                {line.sku ? (
                  <span className="sk:min-w-0 sk:truncate sk:text-[13px] sk:text-fg-tertiary">
                    {line.sku}
                  </span>
                ) : null}
                {line.state ? (
                  <span className="sk:mt-0.5 sk:w-fit sk:rounded sk:bg-fill-secondary sk:px-1.5 sk:py-0.5 sk:text-[13px] sk:font-medium sk:text-fg-secondary">
                    {line.state}
                  </span>
                ) : null}
              </span>
              <span
                className={cn(
                  'sk:shrink-0 sk:font-medium sk:tabular-nums',
                  line.isShort && 'sk:text-danger',
                )}
              >
                ×{line.qty}
                {line.isShort ? ` · ${line.available} in stock` : null}
              </span>
            </span>
          ))}
        </span>
      </span>
    </span>
  );
}

export function FulfillmentCell({
  items,
  providers,
  selectedFP,
  highlightShortages,
  fallbackLabel,
}: FulfillmentCellProps) {
  const summary = getFulfillmentSummary(items, providers, selectedFP, {
    highlightShortages,
  });

  const label = summary.isSplit
    ? 'Multiple'
    : summary.provider?.name || fallbackLabel || 'Unassigned';
  const providerLink = summary.provider?._id
    ? `/app/scm/storage-provider/${encodeURIComponent(summary.provider._id)}`
    : undefined;
  const isUnassigned = summary.providers.length === 0;

  return (
    <StopRowClick>
      <Tooltip
        placement="bottom start"
        content={<Breakdown summary={summary} fallbackLabel={fallbackLabel} />}
      >
        <span
          className={cn(
            'sk:inline-flex sk:max-w-full sk:cursor-default sk:items-center sk:gap-2',
            'sk:rounded-full sk:px-1.5 sk:py-1',
            'sk:hover:inset-shadow-hairline sk:hover:bg-fill-quaternary',
          )}
        >
          {summary.isSplit ? (
            <PackageIcon
              size={12}
              className="sk:shrink-0 sk:text-fg-tertiary"
            />
          ) : isUnassigned ? (
            <span className="sk:flex sk:size-4.5 sk:shrink-0 sk:items-center sk:justify-center sk:text-warning">
              <WarningIcon size={11} />
            </span>
          ) : (
            <ProviderMark provider={summary.provider} name={label} />
          )}

          {providerLink ? (
            <Link
              to={providerLink}
              className="sk:min-w-0 sk:truncate sk:font-medium sk:text-link sk:hover:text-link sk:hover:underline"
            >
              {label}
            </Link>
          ) : (
            <span className="sk:min-w-0 sk:truncate sk:font-medium">
              {label}
            </span>
          )}

          <span
            className={cn(
              'sk:flex sk:size-4.5 sk:shrink-0 sk:items-center sk:justify-center sk:rounded-full',
              'sk:bg-fill-secondary sk:text-[10px] sk:font-semibold sk:text-fg-secondary sk:tabular-nums',
            )}
          >
            {summary.lineCount}
          </span>
        </span>
      </Tooltip>
    </StopRowClick>
  );
}
